import { delay, http, HttpResponse, type RequestHandler } from 'msw';
import { CUSTOMERS, NODES, ROADS, initialState, roadCoordinates } from './data';
import type { CalculationInput, Hypotheses, Pipe, Road, Rule } from './types';

export const handlers: RequestHandler[] = [
  http.get('/api/plan-unifie/demo', () => HttpResponse.json(initialState())),
];

export function effectiveRule(h: Hypotheses, road: Road): Rule {
  return h.local[road.id] ?? h.categories[road.category] ?? { mode: 'weighted', weight: 1 };
}

export function validateCalculation(input: CalculationInput): string | undefined {
  const ids = Object.keys(input.plan.subscribers);
  if (!ids.length) return 'Sélectionnez au moins un abonné.';
  if (ids.some(id => !/^\d{4}-(0[1-9]|1[0-2])$/.test(input.plan.subscribers[id]))) {
    return 'Renseignez le mois et l’année de chaque abonné avant de calculer.';
  }
  const departures = input.hypotheses.departures.filter(d => input.networkKind === 'existing' || d.kind === 'plant');
  if (!departures.length) return 'Définissez au moins un départ possible dans les hypothèses.';
  const rules = [...Object.values(input.hypotheses.categories), ...Object.values(input.hypotheses.local)];
  if (rules.some(r => r.mode === 'weighted' && (!Number.isFinite(r.weight) || r.weight <= 0))) {
    return 'Chaque pondération doit être un nombre strictement positif.';
  }
}

// Simulation locale : chemins pondérés sur le graphe de démonstration, pas le moteur Docker métier.
export function traceNetwork(input: CalculationInput): Pipe[] {
  const error = validateCalculation(input);
  if (error) throw new Error(error);
  const { hypotheses: h } = input;
  const starts = h.departures.filter(d => input.networkKind === 'existing' || d.kind === 'plant');
  const allowed = ROADS.filter(r => effectiveRule(h, r).mode !== 'forbidden');
  const sources = new Set<number>();
  starts.forEach(d => {
    const road = ROADS.find(r => r.id === d.roadId)!;
    if (d.kind === 'network') {
      // An effectively forbidden departure segment cannot silently bypass the prohibition.
      if (effectiveRule(h, road).mode !== 'forbidden') { sources.add(road.from); sources.add(road.to); }
    } else {
      const end = d.coordinates[d.coordinates.length - 1];
      // The fictional connection ends at a snapped position on the road.
      const a = NODES[road.from], b = NODES[road.to];
      const nearFrom = Math.hypot(end[0] - a[0], end[1] - a[1]) <= Math.hypot(end[0] - b[0], end[1] - b[1]);
      sources.add(nearFrom ? road.from : road.to);
    }
  });
  if (!sources.size) throw new Error('Aucun départ utilisable : les tronçons de départ sont interdits.');
  const distances = NODES.map((_, i) => sources.has(i) ? 0 : Infinity);
  const previous: (Road | undefined)[] = NODES.map(() => undefined);
  const visited = new Set<number>();
  for (let i = 0; i < NODES.length; i++) {
    let node = -1;
    distances.forEach((d, n) => {
      if (!visited.has(n) && (node < 0 || d < distances[node])) node = n;
    });
    if (node < 0 || !Number.isFinite(distances[node])) break;
    visited.add(node);
    allowed.filter(r => r.from === node || r.to === node).forEach(road => {
      const next = road.from === node ? road.to : road.from;
      const a = NODES[road.from], b = NODES[road.to];
      const length = Math.hypot((a[0] - b[0]) * Math.cos(a[1] * Math.PI / 180), a[1] - b[1]);
      const rule = effectiveRule(h, road);
      const cost = distances[node] + length * (rule.mode === 'weighted' ? rule.weight : 1);
      if (cost < distances[next]) { distances[next] = cost; previous[next] = road; }
    });
  }
  const selected = new Set<string>();
  const connect = (target: number) => {
    if (!Number.isFinite(distances[target])) throw new Error('Tracé impossible avec ces contraintes. Aucune interdiction n’a été contournée.');
    let node = target;
    while (!sources.has(node)) {
      const road = previous[node];
      if (!road) throw new Error('Impossible de relier le tracé à un départ.');
      selected.add(road.id);
      node = road.from === node ? road.to : road.from;
    }
  };
  CUSTOMERS.filter(c => c.id in input.plan.subscribers).forEach(c => connect(c.node));
  allowed.filter(r => effectiveRule(h, r).mode === 'forced').forEach(road => {
    connect(road.from); connect(road.to); selected.add(road.id);
  });
  return ROADS.filter(r => selected.has(r.id)).map(road => ({
    id: `pipe-${road.id}`, roadId: road.id, name: road.name, manual: false,
    coordinates: roadCoordinates(road),
    month: input.plan.pipes.find(p => !p.manual && p.roadId === road.id)?.month ?? '',
  }));
}

export async function calculate(input: CalculationInput): Promise<Pipe[]> {
  await delay(900);
  if (input.fail) throw new Error('Le moteur de démonstration est indisponible. Le tracé précédent a été conservé.');
  return traceNetwork(input);
}
